/** Network and engine constants. Gameplay balance numbers belong in @two-crowns/data, not here. */
export const TICK_HZ = 20;
export const TICK_MS = 1000 / TICK_HZ;
export const PATCH_HZ = 10;
export const PATCH_MS = 1000 / PATCH_HZ;

/** Remote entities render this far in the past. */
export const INTERP_DELAY_MS = 100;

/**
 * Interest management: a client receives the players, mobs and loot within this radius (plus
 * its party). 120 m rather than the PRD's first 60: the fog hides the edge at 120 m, at 60 m
 * mobs would pop in plainly in view.
 */
export const INTEREST_RADIUS_M = 120;
/** Spatial hash cell size for the interest lookup. */
export const INTEREST_CELL_M = 40;
/** Something already in view stays until this far past the radius (no flicker at the edge). */
export const INTEREST_SLACK_M = 10;
/** How often each client's view is recomputed (a share of the clients every tick). */
export const INTEREST_UPDATE_MS = 200;
/**
 * At most this many new players, mobs and loot enter one client's view per update: walking
 * into a crowd fills in over a second instead of one patch carrying all of it.
 */
export const INTEREST_MAX_ADDS = 40;
/**
 * At most this many players, mobs and loot in one client's view (besides itself, its party and
 * its target): in a crowd only the nearest. Bandwidth and client draw calls grow with it; 80
 * keeps a packed market or a big PvP fight readable on a phone.
 */
export const INTEREST_MAX_VISIBLE = 80;
/** Hostile players rank as if this much closer (x distance) when the view is full. */
export const INTEREST_HOSTILE_WEIGHT = 0.5;

export const RTT_WARN_MS = 250;

/** Per-socket rate limits (PRD: Security & anti-cheat). */
export const RATE_LIMIT_TOTAL_PER_S = 30;
export const RATE_LIMIT_SKILL_PER_S = 10;
export const RATE_LIMIT_CHAT_PER_S = 1;

export const ZONE_ROOM = "zone";

/** Synced status bitmask (PlayerState.status / MobState.status). */
export const StatusFlag = {
  Stun: 1,
  Root: 2,
  Freeze: 4,
  Slow: 8,
  Stealth: 16,
  Dead: 32,
  /** Any harmful effect a cleanse removes (poison, hex, "take more damage"...). */
  Debuff: 64,
} as const;

export const MOB_ID_PREFIX = "m:";

/**
 * Character names: English letters and digits only (no spaces, symbols or look-alike letters),
 * 2 to 12 long so nameplates, chat and rankings stay short on a phone (owner decision 2026-09-30).
 */
export const NAME_MIN = 2;
export const NAME_MAX = 12;

/** WebSocket close codes the game server uses to kick a client. 4000 is Colyseus' consented leave. */
export const KickCode = {
  Cheat: 4001,
  Replaced: 4002,
  RateLimit: 4003,
  /** Kicked or banned by a GM. */
  Moderated: 4004,
} as const;
